
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import WebsiteGroupRepositoryInterface, { WebsiteGroupItem } from '../../../services/WebsiteGroup/interfaces';
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import { UserBaseInfoInterface, UserInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import { PlanRepositoryInterface } from '@/services/Plan/interfaces';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';

@Component({
  components: {
    PageHeader,
  },
  inject: [
    'websiteGroupRepository',
    'websiteRepository',
    'userRepository',
    'planRepository',
    'serverRepository',
  ],
})
export default class AllWebsiteGroups extends Vue {
  public loading: boolean = true;

  public userSearchLoading = true;

  public usersForSelect: (UserInterface | { header: string } | { divider: boolean })[] = [];

  public options: { page: number, itemsPerPage: number } = {
    page: 1,
    itemsPerPage: 25,
  };

  public totalGroupsCount = 0;
  public ownerFilter: string | null = null;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  private websiteGroupRepository!: WebsiteGroupRepositoryInterface;
  private websiteRepository!: WebsiteRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private planRepository!: PlanRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;

  public headers: DataTableHeader[] = [
    { text: 'Main website', value: 'mainWebsite' },
    { text: 'Plan', value: 'plan' },
    { text: 'HB ID', value: 'hbId' },
    { text: 'Server', value: 'server' },
    { text: 'Owner', value: 'owner' },
    { text: 'Sites', value: 'websiteCount', width: '72px' },
  ];

  public groups: WebsiteGroupItem[] = [];
  public websites: Website[] = [];
  public users: UserBaseInfoInterface[] = [];
  public plansMap: { [key: number]: string } = {};

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.loadPlans();
    await this.loadUsersForSelect();
    await this.initialize();
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      const query = this.$route.query;
      const page = Number(query.page);
      this.options.page = page > 0 ? page : 1;

      const pageSize = Number(query.pageSize);
      if (pageSize > 0) {
        this.options.itemsPerPage = pageSize;
      }

      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public buildListQuery(): { [key: string]: string } {
    const query: { [key: string]: string } = {};
    if (this.options.page > 1) {
      query.page = String(this.options.page);
    }
    if (this.options.itemsPerPage > 0 && this.options.itemsPerPage !== 25) {
      query.pageSize = String(this.options.itemsPerPage);
    }
    return query;
  }

  public listQueryEquals(query: { [key: string]: string | (string | null)[] | null | undefined }): boolean {
    const next = this.buildListQuery();
    const keys = ['page', 'pageSize'];
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const left = typeof query[key] === 'string' ? query[key] : '';
      const right = next[key] || '';
      if (left !== right) {
        return false;
      }
    }
    return true;
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = this.buildListQuery();
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'website_groups', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async initialize(): Promise<void> {
    try {
      this.loading = true;

      await this.loadWebsiteGroups();
      await this.loadWebsites();
      await this.loadUsers();
      await this.loadServers();

      this.loading = false;
    } catch (err) {
      this.$errorDialog.show(`An issue ocurred while retrieving website groups. ${err}`);
    }
  }

  public async onPageChange(): Promise<void> {
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.loadWebsiteGroups();
      await this.loadWebsites();
      await this.loadUsers();
      await this.loadServers();
    } catch (err) {
      this.$errorDialog.show(`An issue occurred while retrieving website groups. ${err}`);
    } finally {
      this.loading = false;
    }
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.loadWebsiteGroups();
      await this.loadWebsites();
      await this.loadUsers();
      await this.loadServers();
    } catch (err) {
      this.$errorDialog.show(`An issue occurred while retrieving website groups. ${err}`);
    } finally {
      this.loading = false;
    }
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'website_groups') {
      return;
    }
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.applyRouteQuery();
    this.loading = true;
    try {
      await this.loadWebsiteGroups();
      await this.loadWebsites();
      await this.loadUsers();
      await this.loadServers();
    } catch (err) {
      this.$errorDialog.show(`An issue occurred while retrieving website groups. ${err}`);
    } finally {
      this.loading = false;
    }
  }

  public async loadWebsiteGroups(clearPage?: boolean): Promise<void> {
    try {
      this.loading = true;
      if (clearPage) {
        this.options.page = 1;
      }

      const groupsData = await this.websiteGroupRepository.searchGroups(
        this.ownerFilter,
        this.options.page,
        this.options.itemsPerPage > 0 ? this.options.itemsPerPage : 25,
      );

      if (!groupsData?.groups) {
        this.groups = [];
        this.totalGroupsCount = 0;
        this.loading = false;
        return;
      }

      this.groups = groupsData.groups;
      this.totalGroupsCount = groupsData.count;

      if (clearPage) {
        await this.syncRouteQuery();
        await this.loadWebsites();
        await this.loadUsers();
        await this.loadServers();
      }

      this.loading = false;
    } catch (err) {
      this.groups = [];
      this.totalGroupsCount = 0;
      this.$errorDialog.show(`An issue occurred while retrieving website groups. ${err}`);
    } finally {
      this.loading = false;
    }
  }

  private async loadWebsites(): Promise<void> {
    const ids = this.groups.map((group) => group.mainWebsite);
    if (!ids.length) {
      return;
    }

    this.websites = await this.websiteRepository.getWebsitesByIds(ids);
  }

  private async loadUsers(): Promise<void> {
    const ids = this.groups.map((group) => group.owner).filter((owner) => owner.length === 24);
    if (!ids.length) {
      return;
    }

    this.users = await this.userRepository.getUsersByIds(ids);
  }

  private async loadUsersForSelect(): Promise<void> {
    try {
      const usersResponse = await this.userRepository.getAllUsers(0);
      const usersForSelect: (UserInterface | { header: string } | { divider: boolean })[] = [{ header: 'Fenix' }];
      usersForSelect.push(...usersResponse[0]);

      const hbOwners = await this.websiteGroupRepository.getHBOwners();
      usersForSelect.push({ divider: true }, { header: 'Hostbill' });

      usersForSelect.push(
        ...hbOwners.map((owner) => ({ id: owner, username: `HB ${owner}` } as unknown as UserInterface)),
      );

      this.usersForSelect = usersForSelect;
      this.userSearchLoading = false;
    } catch (err) {
      this.$errorDialog.show(err.message);
    }
  }

  public async loadPlans(): Promise<void> {
    const plans = await this.planRepository.getAll();
    plans.forEach((plan) => {
      this.plansMap[plan.id] = plan.name;
    });
  }

  public async loadServers(): Promise<void> {
    const serversIds: string[] = [];
    this.websites.forEach((website) => {
      if (!serversIds.includes(website.originServer)) {
        serversIds.push(website.originServer);
      }
    });

    if (!serversIds.length) {
      return;
    }

    const servers = await this.serverRepository.getServersByIds(serversIds);
    const serversMap: { [key: string]: string } = {};
    servers.forEach((server) => {
      serversMap[server._id] = server.hostname;
    });

    this.websites.forEach((website) => {
      const groupIndex = this.groups.findIndex((g) => g.mainWebsite === website.id);
      if (groupIndex === -1) {
        return;
      }

      this.$set(this.groups[groupIndex], 'server', serversMap[website.originServer]);
    });
  }

  private getWebsiteDomain(websiteId: number): string {
    const website = this.websites.find((w) => w.id === websiteId);
    if (!website) {
      return '';
    }

    return website.domain;
  }

  public openGroup(item: WebsiteGroupItem): void {
    this.$router.push({ name: 'website_group_view', params: { id: String(item.id) } });
  }

  private getUserEmail(userId: string): string {
    const user = this.users.find((w) => w.id === userId);
    if (!user) {
      return '';
    }

    return user.email;
  }
}
